/**
 * components/legal/vop/vopCompanions.ts
 *
 * The four pages beside the general terms that counsel did not draft: how
 * to pay, the recurring-payment terms, the complaints procedure and the
 * contact page. They are the company's own pages, kept because somebody outside the
 * contract reads them by address.
 *
 * ## Why the complaints procedure and the recurring terms stay pages
 *
 * Counsel's terms replace both (clause 21.6): complaints are their article
 * 11, recurring payments their clauses 14.7 to 14.9. But the payment gateway
 * asks for each by URL — a complaints procedure of its own, and recurring
 * terms on a page that "must not be identical to the General Terms and
 * Conditions" — and a cardholder agreeing to automatic debits is owed the
 * mandate on one page. So both stay, as what they now are: a restatement of
 * the article that governs, which says so and yields to the terms where the
 * two differ. Neither grants anything the terms do not. The procedure in
 * force did (a refund where a complaint ran late, a consumer's forum), which
 * is why the complaints page is written anew from article 11 and not derived.
 *
 * ## What the terms no longer say, and the gateway still reads
 *
 * Counsel's terms name no card scheme, no gateway and no territorial reach,
 * and say nothing of delivery beyond when a period starts. The gateway
 * checks for all of them on the merchant's public pages, so the payment page
 * says them: how to pay (Visa, Mastercard, GoPay, 3-D Secure), delivery and
 * territorial reach. Counsel's price list (Annex 3) says nothing else, at
 * counsel's request, so these have a page of their own, which yields to the
 * terms and the price list.
 *
 * Each page is derived from the page in force where that page still holds,
 * so that a sentence is written once; `vopSet.test.ts` holds what
 * the gateway reads.
 */

import { COMPANY, formattedAddress } from "../companyDetails";
import type { LegalDoc, LegalSection } from "../legalContent";
import { vopEffectiveDate } from "./effectiveDate";

type Language = "cs" | "en";

/** A document's sections with some replaced by id, in place. A replacement for an id the document lacks is a mistake. */
function withSections(doc: LegalDoc, replacements: Record<string, LegalSection>): LegalSection[] {
    const known = new Set(doc.sections.map((section) => section.id));
    for (const id of Object.keys(replacements)) {
        if (!known.has(id)) throw new Error(`"${doc.title}" has no "${id}" section to replace`);
    }
    return doc.sections.map((section) => replacements[section.id] ?? section);
}

/**
 * How to pay and how the service is delivered. What the gateway reads beside
 * a price used to stand on the price list; counsel's price list (Annex 3)
 * holds only the contract, so it is said here, and yields to the terms and
 * the price list where the two differ.
 */
export function buildVopPayment(lang: Language): LegalDoc {
    const cs = lang === "cs";
    return {
        title: cs ? "Platba a dodání služby" : "Payment and delivery",
        effectiveDate: vopEffectiveDate(lang),
        intro: [
            cs
                ? "Tato stránka shrnuje, jak se za službu Optivo platí a jak se dodává. Ceny, Tarify a zkušební období stanoví Ceník (/subscription), Příloha č. 3 Všeobecných obchodních podmínek (/terms); platbu a předplatné upravuje jejich článek 14. Při rozporu mají přednost Všeobecné obchodní podmínky a Ceník. Služba je určena výhradně podnikatelům, nikoli spotřebitelům."
                : "This page sets out how the Optivo service is paid for and how it is delivered. The prices, the Plans and the trial period are laid down by the Price List (/subscription), Annex 3 of the General Terms and Conditions (/terms); payment and the subscription are governed by their Article 14. Where the two differ, the General Terms and Conditions and the Price List take precedence. The service is intended solely for businesses, not for consumers.",
        ],
        sections: [
            {
                id: "how-to-pay",
                heading: cs ? "Jak se platí" : "How to pay",
                paragraphs: [
                    cs
                        ? "Před zahájením platby se v aplikaci v sekci Předplatné zobrazí cena za další období (měsíc, nebo rok) včetně rozpisu. Cena je splatná předem za celé období, jedním z těchto způsobů:"
                        : "Before a payment is started, the Billing section of the application shows the price for the coming period (a month, or a year) with its breakdown. The price is payable in advance for the whole period, in one of these ways:",
                ],
                bullets: cs
                    ? [
                          "Platební kartou Visa nebo Mastercard prostřednictvím platební brány GoPay, zabezpečenou technologií 3-D Secure (Visa Secure, Mastercard Identity Check).",
                          "Bankovním převodem na účet poskytovatele, s platebním QR kódem a variabilním symbolem uvedeným v aplikaci.",
                      ]
                    : [
                          "By Visa or Mastercard payment card through the GoPay payment gateway, secured by 3-D Secure (Visa Secure, Mastercard Identity Check).",
                          "By bank transfer to the provider's account, with the payment QR code and variable symbol shown in the application.",
                      ],
                notes: cs
                    ? [
                          "Platební bránu provozuje společnost GoPay s.r.o. Údaje o platební kartě zadáváte přímo v prostředí brány; poskytovatel k nim nemá přístup a neukládá je.",
                          "Ke každé uhrazené platbě je vystaven daňový doklad, který je ke stažení přímo v aplikaci.",
                      ]
                    : [
                          "The payment gateway is operated by GoPay s.r.o. Card details are entered directly in the gateway; the provider has no access to them and does not store them.",
                          "A tax document is issued for every settled payment and can be downloaded in the application.",
                      ],
            },
            {
                id: "delivery",
                heading: cs ? "Dodání služby" : "Delivery of the service",
                paragraphs: cs
                    ? [
                          "Služba se dodává výhradně elektronicky, zpřístupněním v aplikaci. Předplacené období začíná okamžikem přijetí platby kartou, u bankovního převodu připsáním částky na účet poskytovatele, zpravidla následující pracovní den. Nedodává se žádné hmotné zboží, nevzniká proto poštovné ani jiné náklady na dodání.",
                          "Na službu se nevztahuje žádné územní ani vývozní omezení: jde o software provozovaný v cloudu, dostupný odkudkoli přes internet. Za dodržení právních předpisů státu, ve kterém službu užíváte, odpovídáte vy.",
                      ]
                    : [
                          "The service is delivered exclusively electronically, by being made available in the application. A prepaid period starts at the moment a card payment is received or, for a bank transfer, once the amount is credited to the provider's account, usually the next business day. No physical goods are delivered, so no postage or other delivery costs arise.",
                          "No territorial or export restriction applies to the service: it is cloud software reachable from anywhere over the internet. Compliance with the law of the country in which you use the service is your responsibility.",
                      ],
            },
            {
                id: "recurring",
                heading: cs ? "Automatické obnovování" : "Automatic renewal",
                paragraphs: cs
                    ? [
                          "Při platbě kartou si můžete zaškrtnout automatické obnovování. Tím udělíte souhlas s tím, aby se předplatné na další období strhávalo z karty samo, vždy v den, kdy končí zaplacené období — u měsíčního předplatného tedy jednou měsíčně, u ročního jednou za rok.",
                          "Částka se řídí objekty, které předplatné pokrývá, a počtem jejich pokojů: změní se jen tehdy, když se změní ony, individuálně sjednaná služba nebo ceník. Aktuální částka je vždy vidět v aplikaci ještě před stržením.",
                          "Automatické obnovování je dobrovolné — bez něj platíte každé období ručně. Zrušit je můžete kdykoli jedním kliknutím v sekci Předplatné a platí to okamžitě.",
                          "Úplné podmínky opakovaných plateb najdete na /recurring-terms.",
                      ]
                    : [
                          "When paying by card you can tick automatic renewal. That gives consent for the next period's subscription to be charged to your card automatically, on the day your paid period ends — once a month on a monthly subscription, once a year on a yearly one.",
                          "The amount follows the properties your subscription covers and the number of their rooms: it changes only when they, an individually agreed service or the price list change. The current amount is always visible in the application before it is charged.",
                          "Automatic renewal is optional — without it you pay for each period manually. You can cancel it at any time with one click in the Billing section, and it takes effect immediately.",
                          "The full recurring payment terms are at /recurring-terms.",
                      ],
            },
            {
                id: "cancellation",
                heading: cs ? "Ukončení" : "Cancellation",
                paragraphs: cs
                    ? [
                          "Uplynutím zaplaceného období smlouva nekončí. Nezaplatíte-li další období, zaplacené období doběhne a další se neúčtuje; smlouvu můžete kdykoli vypovědět. Trvání a ukončení smlouvy upravuje článek 15 Všeobecných obchodních podmínek.",
                          `Dotazy k platbám: ${COMPANY.email}. Všeobecné obchodní podmínky: /terms.`,
                      ]
                    : [
                          "The contract does not end when a paid period runs out. If you do not pay for a further period, the paid period runs out and no further period is charged; you may terminate the contract at any time. The term and the end of the contract are governed by Article 15 of the General Terms and Conditions.",
                          `Questions about payments: ${COMPANY.email}. General Terms and Conditions: /terms.`,
                      ],
            },
        ],
    };
}

/** The recurring-payment terms: what clauses 14.7 to 14.9 of the terms mean for a cardholder. */
export function buildVopRecurring(lang: Language, inForce: LegalDoc): LegalDoc {
    const cs = lang === "cs";
    const documents = inForce.sections.find((section) => section.id === "documents");
    return {
        title: inForce.title,
        effectiveDate: vopEffectiveDate(lang),
        intro: [
            cs
                ? `Tento dokument shrnuje pro držitele karty, jak u služby ${COMPANY.brandName} vzniká souhlas s opakovanými platbami z karty, jak se platby strhávají a jak je zrušit. Opakované platby upravuje článek 14 Všeobecných obchodních podmínek (/terms); při rozporu mají přednost Všeobecné obchodní podmínky.`
                : `This document sets out for the cardholder how consent to recurring card payments arises for ${COMPANY.brandName}, how the payments are charged and how to cancel them. Recurring payments are governed by Article 14 of the General Terms and Conditions (/terms); where the two differ, the General Terms and Conditions take precedence.`,
        ],
        sections: withSections(inForce, {
            documents: {
                id: "documents",
                heading: documents?.heading ?? "",
                paragraphs: documents?.paragraphs,
                bullets: cs
                    ? [
                          "Tyto podmínky: /recurring-terms",
                          "Ceník: /subscription",
                          "Platba a dodání služby: /payment",
                          "Všeobecné obchodní podmínky: /terms",
                          "Ochrana osobních údajů: /privacy",
                      ]
                    : [
                          "These terms: /recurring-terms",
                          "Price List: /subscription",
                          "Payment and delivery: /payment",
                          "General Terms and Conditions: /terms",
                          "Personal data protection: /privacy",
                      ],
            },
        }),
    };
}

/** The complaints procedure: article 11 of the terms, for somebody who has a complaint to make. */
export function buildVopComplaints(lang: Language): LegalDoc {
    if (lang === "cs") {
        return {
            title: "Reklamační řád",
            effectiveDate: vopEffectiveDate("cs"),
            intro: [
                `Tento reklamační řád shrnuje, jak u služby ${COMPANY.brandName} uplatnit vadu služby nebo chybu vyúčtování, v jaké lhůtě je reklamace vyřízena a co vadou není. Práva z vadného plnění upravuje článek 11 Všeobecných obchodních podmínek (/terms); při rozporu mají přednost Všeobecné obchodní podmínky.`,
            ],
            sections: [
                {
                    id: "scope",
                    heading: "1. Čeho se reklamační řád týká",
                    paragraphs: [
                        "Služba je poskytována výhradně elektronicky a je určena podnikatelům. Nedodává se žádné hmotné zboží, a proto se neuplatní ustanovení o vrácení zboží, o poštovném ani o dopravě: reklamace se uplatňuje na dálku a nic se nikam nezasílá.",
                        "Reklamovat lze vadu samotné služby a chybu ve vyúčtování nebo na daňovém dokladu. Pro obojí platí stejný postup i lhůty.",
                    ],
                },
                {
                    id: "conformity",
                    heading: "2. Kdy je služba bez vad",
                    paragraphs: [
                        "Služba je bez vad, odpovídá-li Specifikaci služby (/specification) a parametrům dostupnosti podle Všeobecných obchodních podmínek.",
                        "Poskytovatel odpovídá za technickou chybu propisu cen do rezervačního systému, kterou se rozumí:",
                    ],
                    bullets: [
                        "propsání jiné hodnoty, než kterou služba vypočetla, nebo než ceny, kterou uživatel ručně zadal či zamkl;",
                        "propis bez pokynu uživatele, zejména po vypnutí automatického odesílání nebo mimo nastavené období či rozsah;",
                        "propsání vypočtené ceny mimo cenové limity, ledaže jde o odchylku, kterou předpokládá Specifikace služby nebo nastavení uživatele.",
                    ],
                    notes: [
                        "Technickou chybou není propis vypočtené ceny v souladu se Specifikací služby a nastavením uživatele ani chyba způsobená rezervačním systémem, channel managerem nebo prodejním kanálem.",
                    ],
                },
                {
                    id: "exclusions",
                    heading: "3. Co vadou není",
                    paragraphs: ["Vadou služby není:"],
                    bullets: [
                        "odchylka vypočtené ceny od skutečného tržního vývoje nebo od očekávání uživatele;",
                        "výsledek odpovídající Specifikaci služby a nastavení uživatele;",
                        "nepřesnost dat poskytnutých třetími stranami;",
                        "omezení vyplývající z rozhraní třetích stran;",
                        "výpadek internetového připojení, zařízení nebo prohlížeče na straně uživatele;",
                        "důsledek ceny, kterou uživatel ručně zadal nebo přepsal;",
                        "nedostupnost nebo omezení služby při předem oznámené plánované údržbě;",
                        "vada, na kterou byl uživatel předem upozorněn nebo pro kterou byla cena snížena;",
                        "vada způsobená užitím služby v rozporu se Všeobecnými obchodními podmínkami.",
                    ],
                    notes: [
                        "Poskytovatel dále neodpovídá za dostupnost, funkčnost a změny rezervačních systémů, channel managerů, prodejních kanálů a poskytovatele cenových dat ani za vyšší moc.",
                    ],
                },
                {
                    id: "how-to-file",
                    heading: "4. Jak a kdy reklamaci uplatnit",
                    paragraphs: [
                        `Vadu vytkněte bez zbytečného odkladu, nejpozději do 15 dnů ode dne, kdy jste ji zjistili nebo při náležité péči měli zjistit. Reklamaci uplatněte e-mailem na ${COMPANY.email} nebo písemně na adresu sídla: ${COMPANY.legalName}, ${formattedAddress("cs")}. Zvláštní formulář se nevyžaduje.`,
                        "Reklamace obsahuje:",
                    ],
                    bullets: [
                        "identifikaci účtu a objektu;",
                        "popis vady a dobu, kdy se projevila;",
                        "dotčené noci a typy ubytovacích jednotek;",
                        "u reklamace vyúčtování číslo daňového dokladu nebo variabilní symbol platby.",
                    ],
                    notes: ["Chybí-li údaje nezbytné k posouzení reklamace, běží lhůta k jejímu vyřízení až od jejich doplnění."],
                },
                {
                    id: "deadlines",
                    heading: "5. Lhůty",
                    bullets: [
                        "Přijetí reklamace poskytovatel potvrdí do 3 pracovních dnů.",
                        "Reklamaci vyřídí bez zbytečného odkladu, nejpozději do 30 dnů od jejího doručení.",
                        "O vyřízení informuje e-mailem; neuzná-li reklamaci, uvede důvody.",
                    ],
                },
                {
                    id: "outcome",
                    heading: "6. Způsob vyřízení",
                    bullets: [
                        "Odstranění vady.",
                        "Přiměřená sleva z ceny za dobu trvání vady, není-li vada odstraněna v přiměřené lhůtě.",
                        "U podstatné vady také výpověď smlouvy bez výpovědní doby a vrácení poměrné části předplacené ceny.",
                        "Oprava vyúčtování a vystavení opravného daňového dokladu, jde-li o vadu vyúčtování.",
                    ],
                    notes: [
                        "Poměrnou část ceny vrací poskytovatel do 14 dnů stejným platebním prostředkem, jakým byla cena uhrazena, nedohodnou-li se strany jinak. Náhradu újmy a její omezení upravuje článek 12 Všeobecných obchodních podmínek.",
                    ],
                },
                {
                    id: "documents",
                    heading: "7. Související dokumenty",
                    bullets: [
                        "Všeobecné obchodní podmínky: /terms",
                        "Specifikace služby: /specification",
                        "Ceník: /subscription",
                        "Kontaktní údaje: /contact",
                        "Ochrana osobních údajů: /privacy",
                    ],
                },
            ],
        };
    }
    return {
        title: "Complaints procedure",
        effectiveDate: vopEffectiveDate("en"),
        intro: [
            `This complaints procedure sets out how to raise a defect of the ${COMPANY.brandName} service or an error in billing, within what time a complaint is settled, and what is not a defect. Rights from defective performance are governed by Article 11 of the General Terms and Conditions (/terms); where the two differ, the General Terms and Conditions take precedence.`,
        ],
        sections: [
            {
                id: "scope",
                heading: "1. What this procedure covers",
                paragraphs: [
                    "The service is provided exclusively by electronic means and is intended for businesses. No physical goods are delivered, so the rules on returning goods, on postage and on carriage do not apply: a complaint is made remotely and nothing is sent anywhere.",
                    "A complaint may concern a defect of the service itself, or an error in billing or on a tax document. The same procedure and time limits apply to both.",
                ],
            },
            {
                id: "conformity",
                heading: "2. When the service is free of defects",
                paragraphs: [
                    "The service is free of defects if it conforms to the Service Specification (/specification) and to the availability parameters under the General Terms and Conditions.",
                    "The provider is liable for a technical error in the push of prices to the reservation system, which means:",
                ],
                bullets: [
                    "the push of a value other than the one the service calculated, or than the price the user entered by hand or locked;",
                    "a push without the user's instruction, in particular after automatic sending was switched off or outside the period or scope set;",
                    "the push of a calculated price outside the price limits, unless it is a departure provided for by the Service Specification or the user's settings.",
                ],
                notes: [
                    "A push of a calculated price in accordance with the Service Specification and the user's settings is not a technical error, nor is an error caused by the reservation system, a channel manager or a sales channel.",
                ],
            },
            {
                id: "exclusions",
                heading: "3. What is not a defect",
                paragraphs: ["The following is not a defect of the service:"],
                bullets: [
                    "a deviation of a calculated price from the actual development of the market or from the user's expectations;",
                    "a result that conforms to the Service Specification and to the user's settings;",
                    "an inaccuracy of data supplied by third parties;",
                    "a limitation arising from third-party interfaces;",
                    "an outage of the internet connection, device or browser on the user's side;",
                    "the consequence of a price the user entered or overwrote by hand;",
                    "the unavailability or limitation of the service during planned maintenance announced in advance;",
                    "a defect the user was warned of in advance or on account of which the price was reduced;",
                    "a defect caused by use of the service in breach of the General Terms and Conditions.",
                ],
                notes: [
                    "The provider is further not responsible for the availability, functioning and changes of reservation systems, channel managers, sales channels and the provider of price data, nor for force majeure.",
                ],
            },
            {
                id: "how-to-file",
                heading: "4. How and when to make a complaint",
                paragraphs: [
                    `Give notice of a defect without undue delay, no later than 15 days from the day you found it or, exercising due care, should have found it. Make the complaint by e-mail to ${COMPANY.email} or in writing to the registered seat: ${COMPANY.legalName}, ${formattedAddress("en")}. No special form is required.`,
                    "A complaint contains:",
                ],
                bullets: [
                    "identification of the account and of the property;",
                    "a description of the defect and the time at which it appeared;",
                    "the nights and types of accommodation units affected;",
                    "for a complaint about billing, the number of the tax document or the variable symbol of the payment.",
                ],
                notes: ["If details necessary to assess the complaint are missing, the period for settling it runs only from the time they are supplied."],
            },
            {
                id: "deadlines",
                heading: "5. Time limits",
                bullets: [
                    "The provider confirms receipt of a complaint within 3 business days.",
                    "It settles the complaint without undue delay, no later than 30 days from its delivery.",
                    "It informs you of the settlement by e-mail; if it does not uphold the complaint, it states the reasons.",
                ],
            },
            {
                id: "outcome",
                heading: "6. How a complaint is settled",
                bullets: [
                    "Removal of the defect.",
                    "A reasonable reduction of the price for the time the defect lasted, if the defect is not removed within a reasonable period.",
                    "For a material defect, also termination of the contract without a notice period and a refund of the proportionate part of the prepaid price.",
                    "Correction of the billing and a corrective tax document, where the defect is one of billing.",
                ],
                notes: [
                    "The provider refunds the proportionate part of the price within 14 days by the same means of payment by which the price was paid, unless the parties agree otherwise. Compensation for damage and its limitation are governed by Article 12 of the General Terms and Conditions.",
                ],
            },
            {
                id: "documents",
                heading: "7. Related documents",
                bullets: [
                    "General Terms and Conditions: /terms",
                    "Service Specification: /specification",
                    "Price list: /subscription",
                    "Contact details: /contact",
                    "Personal data protection: /privacy",
                ],
            },
        ],
    };
}

/** The contact page, pointing at the documents of the set. */
export function buildVopContact(lang: Language, inForce: LegalDoc): LegalDoc {
    const cs = lang === "cs";
    const documents = inForce.sections.find((section) => section.id === "documents");
    return {
        title: inForce.title,
        effectiveDate: vopEffectiveDate(lang),
        intro: inForce.intro,
        sections: withSections(inForce, {
            complaints: {
                id: "complaints",
                heading: cs ? "Reklamace a ukončení smlouvy" : "Complaints and ending the contract",
                paragraphs: [
                    cs
                        ? `Reklamace, výpovědi i odstoupení od smlouvy přijímáme na ${COMPANY.email} nebo písemně na adresu sídla. Reklamaci vyřídíme do 30 dnů; postup shrnuje reklamační řád na /complaints.`
                        : `Complaints, notices of termination and withdrawals from the contract are accepted at ${COMPANY.email} or in writing at the registered seat. A complaint is settled within 30 days; the procedure is set out at /complaints.`,
                ],
            },
            documents: {
                id: "documents",
                heading: documents?.heading ?? "",
                bullets: cs
                    ? [
                          "Všeobecné obchodní podmínky: /terms",
                          "Specifikace služby: /specification",
                          "Smlouva o zpracování osobních údajů: /dpa",
                          "Ceník: /subscription",
                          "Platba a dodání služby: /payment",
                          "Reklamační řád: /complaints",
                          "Zásady ochrany osobních údajů: /privacy",
                      ]
                    : [
                          "General Terms and Conditions: /terms",
                          "Service Specification: /specification",
                          "Data Processing Agreement: /dpa",
                          "Price List: /subscription",
                          "Payment and delivery: /payment",
                          "Complaints procedure: /complaints",
                          "Privacy notice: /privacy",
                      ],
            },
        }),
    };
}
